
"use client";

import { useEffect, useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import { Rabbit, BaggageClaim, Plane, LogOut, Sprout, TrendingUp } from "lucide-react";
import {
  collection,
  doc,
  serverTimestamp,
  addDoc,
  deleteDoc,
  getDoc,
  query,
  where,
  writeBatch,
  updateDoc,
  Timestamp,
} from "firebase/firestore";

import ExpenseCard from "@/components/expense-card";
import type { Expense } from "@/types/expense";
import { FirebaseClientProvider, useFirebase, useUser, useFirestore, useMemoFirebase, useCollection } from "@/firebase";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";
import { useLocalStorage } from "@/hooks/use-local-storage";

function HomePageContent() {
  const router = useRouter();
  const { auth, firestore } = useFirebase();
  const { user, isUserLoading } = useUser();
  const [userName, setUserName] = useState<string>("");
  const [showPreviousYear, setShowPreviousYear] = useLocalStorage('showPreviousYear', true);

  const personalExpensesCollection = useMemoFirebase(() => {
    if (!firestore || !user?.uid) return null;
    return collection(firestore, "users", user.uid, "personal_expenses");
  }, [firestore, user?.uid]);

  const workExpensesQuery = useMemoFirebase(() => {
    if (!firestore || !user?.uid) return null;
    // Only show work expenses that have not been cleared
    return query(collection(firestore, "users", user.uid, "work_expenses"), where("cleared", "==", false));
  }, [firestore, user?.uid]);
  
  const pastWorkExpensesQuery = useMemoFirebase(() => {
    if (!firestore || !user?.uid) return null;
    // Only show work expenses that HAVE been cleared
    return query(collection(firestore, "users", user.uid, "work_expenses"), where("cleared", "==", true));
  }, [firestore, user?.uid]);

  const allWorkExpensesQuery = useMemoFirebase(() => {
    if (!firestore || !user?.uid) return null;
    // For CSV export, we need all expenses
    return collection(firestore, "users", user.uid, "work_expenses");
  }, [firestore, user?.uid]);

  const { data: personalExpenses, isLoading: personalLoading } = useCollection<Expense>(personalExpensesCollection);
  // This hook will show only non-cleared expenses
  const { data: currentWorkExpenses, isLoading: workLoading } = useCollection<Expense>(workExpensesQuery);
  // This hook will show only cleared expenses for the history view
  const { data: pastWorkExpenses, isLoading: pastWorkLoading } = useCollection<Expense>(pastWorkExpensesQuery);
  // We need a separate fetch for all expenses for the CSV export
  const { data: allWorkExpenses, isLoading: allWorkExpensesLoading } = useCollection<Expense>(allWorkExpensesQuery);


  const userProfileDoc = useMemoFirebase(() => {
    if (!firestore || !user?.uid) return null;
    return doc(firestore, "users", user.uid);
  }, [firestore, user?.uid]);

  const totalPersonalExpenses = useMemo(() => {
    return personalExpenses?.reduce((acc, expense) => acc + expense.amount, 0) || 0;
  }, [personalExpenses]);

  const totalWorkExpenses = useMemo(() => {
    return currentWorkExpenses?.reduce((acc, expense) => acc + expense.amount, 0) || 0;
  }, [currentWorkExpenses]);

  const totalExpenses = totalPersonalExpenses + totalWorkExpenses;

  const { ytdTotal, ytdLabel } = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const isNewYear = new Date().getMonth() === 0 && new Date().getDate() === 1;

    let total = 0;
    let label = `YTD ${currentYear}`;

    if (!allWorkExpenses) {
      return { ytdTotal: 0, ytdLabel: label };
    }

    const previousYearExpenses = allWorkExpenses.filter(expense => {
      const expenseYear = (expense.date as any)?.toDate?.().getFullYear() || new Date(expense.date as string).getFullYear();
      return expenseYear === currentYear - 1;
    });
    
    const currentYearExpenses = allWorkExpenses.filter(expense => {
      const expenseYear = (expense.date as any)?.toDate?.().getFullYear() || new Date(expense.date as string).getFullYear();
      return expenseYear === currentYear;
    });

    if (isNewYear && showPreviousYear && currentYearExpenses.length === 0) {
      total = previousYearExpenses.reduce((acc, exp) => acc + exp.amount, 0);
      label = `Total ${currentYear - 1}`;
    } else {
      total = currentYearExpenses.reduce((acc, exp) => acc + exp.amount, 0);
      label = `YTD ${currentYear}`;
    }
    
    return { ytdTotal: total, ytdLabel: label };
  }, [allWorkExpenses, showPreviousYear]);
  

  useEffect(() => {
    if (!isUserLoading && !user) {
      router.push("/login");
    }
  }, [isUserLoading, user, router]);
  
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (userProfileDoc) {
        try {
          const docSnap = await getDoc(userProfileDoc);
          if (docSnap.exists()) {
            setUserName(docSnap.data().name || "");
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        }
      }
    };
    if (user) {
      fetchUserProfile();
    }
  }, [user, userProfileDoc]);


  const addExpense = (type: "personal" | "work", values: { amount: number; description: string; retailer?: string; employer?: string }) => {
    const { amount, description, retailer, employer } = values;
    
    // Automatically switch to current year if adding a new expense
    if (new Date().getFullYear() > new Date().getFullYear() -1 && showPreviousYear) {
      setShowPreviousYear(false);
    }

    const expenseData: any = {
      amount,
      description,
      date: serverTimestamp(),
    };

    if (type === "personal") {
       if (personalExpensesCollection) {
        if(retailer) expenseData.retailer = retailer;
        addDoc(personalExpensesCollection, expenseData).catch(error => {
            console.error("Error adding personal expense: ", error);
        });
      }
    } else {
      if (firestore && user) {
        const workExpensesCollection = collection(firestore, "users", user.uid, "work_expenses");
        const workExpenseData = { ...expenseData, cleared: false };
        if(retailer) workExpenseData.retailer = retailer;
        if(employer) workExpenseData.employer = employer;
        addDoc(workExpensesCollection, workExpenseData).catch(error => {
            console.error("Error adding work expense: ", error);
        });
      }
    }
  };

  const updateExpense = (type: "personal" | "work", id: string, values: { amount: number; description: string; retailer?: string; employer?: string; date: Date }) => {
    if (!firestore || !user) return;
    const collectionName = type === 'personal' ? 'personal_expenses' : 'work_expenses';
    const docRef = doc(firestore, "users", user.uid, collectionName, id);
    
    const updateData: any = {
      amount: values.amount,
      description: values.description,
      retailer: values.retailer || "",
      date: Timestamp.fromDate(values.date),
    };

    if (type === 'work' && values.employer) {
      updateData.employer = values.employer;
    }

    updateDoc(docRef, updateData).catch(error => {
      console.error(`Error updating ${type} expense: `, error);
    });
  };

  const deleteExpense = (type: "personal" | "work", id: string) => {
    if (!firestore || !user) return;
    const collectionName = type === 'personal' ? 'personal_expenses' : 'work_expenses';
    const docRef = doc(firestore, "users", user.uid, collectionName, id);
    deleteDoc(docRef).catch(error => {
        console.error(`Error deleting ${type} expense: `, error);
    });
  };

  const clearAllPersonalExpenses = async () => {
    if (!firestore || !user || !personalExpenses || personalExpenses.length === 0) return;

    const batch = writeBatch(firestore);
    personalExpenses.forEach(expense => {
      const docRef = doc(firestore, "users", user.uid, 'personal_expenses', expense.id);
      batch.delete(docRef);
    });
    
    try {
      await batch.commit();
    } catch (error) {
      console.error("Error clearing personal expenses:", error);
    }
  }

  const resetWorkPayPeriod = async () => {
    if (!firestore || !user || !currentWorkExpenses || currentWorkExpenses.length === 0) return;
  
    // Use a batch write for efficiency
    const batch = writeBatch(firestore);
    currentWorkExpenses.forEach(expense => {
      const docRef = doc(firestore, "users", user.uid, "work_expenses", expense.id);
      batch.update(docRef, { cleared: true });
    });
  
    try {
      await batch.commit();
    } catch (error) {
      console.error("Error resetting work pay period: ", error);
    }
  };

  const handleLogout = () => {
    auth?.signOut();
    router.push("/login");
  };
  
  if (isUserLoading || !user || personalLoading || workLoading || allWorkExpensesLoading || pastWorkLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 z-10 bg-background/80"></div>
        <div className="absolute inset-0 z-0 grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-4 p-4 opacity-20">
          {Array.from({ length: 200 }).map((_, i) => (
            <Rabbit
              key={i}
              className="text-primary"
              style={{
                width: `${2 + (i % 5)}rem`,
                height: `${2 + (i % 5)}rem`,
                transform: `rotate(${i * 15}deg)`,
              }}
            />
          ))}
        </div>
      </div>

      <header className="z-20 bg-background/50 backdrop-blur-sm pt-4">
        <div className="container mx-auto flex items-center justify-between gap-2 px-4 py-4 sm:px-6 md:px-8">
          <div className="flex items-center gap-2 ml-2">
            <Rabbit className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Shizuka Treasury Co</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-2xl font-medium text-foreground">Hello, {userName || user.email}</span>
            <Button variant="ghost" size="icon" onClick={handleLogout} title="Log Out">
              <LogOut className="h-5 w-5"/>
            </Button>
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 sm:px-6 md:px-8 pt-4 pb-4 sm:pb-6 md:pb-8 flex-1 z-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="md:col-span-2">
                <Card className="backdrop-blur border-white/10 bg-card/5">
                    <CardHeader className="flex-row items-center gap-4 space-y-0">
                        <Sprout className="h-8 w-8 text-primary" />
                        <div className="flex-1">
                            <CardTitle>Current Expenses</CardTitle>
                        </div>
                        <div className="text-4xl font-bold text-primary">{formatCurrency(totalExpenses)}</div>
                    </CardHeader>
                </Card>
            </div>
            <div className="md:col-span-1">
              <ExpenseCard
                title="Personal Expenses"
                icon={<BaggageClaim className="h-6 w-6 text-primary" />}
                expenses={personalExpenses || []}
                onAddExpense={(values) => addExpense("personal", values)}
                onUpdateExpense={(id, values) => updateExpense("personal", id, values)}
                onDeleteExpense={(id) => deleteExpense("personal", id)}
                onClearAll={() => clearAllPersonalExpenses()}
              />
            </div>
            <div className="md:col-span-1">
              <ExpenseCard
                title="Work Expenses"
                icon={<Plane className="h-6 w-6 text-primary" />}
                expenses={currentWorkExpenses || []}
                pastExpenses={pastWorkExpenses || []}
                allExpensesForExport={allWorkExpenses || []}
                onAddExpense={(values) => addExpense("work", values)}
                onUpdateExpense={(id, values) => updateExpense("work", id, values)}
                onDeleteExpense={(id) => deleteExpense("work", id)}
                onClearAll={() => resetWorkPayPeriod()}
                isWorkCard={true}
              />
            </div>
            <div className="md:col-span-2">
                <Card className="backdrop-blur border-white/10 bg-card/5">
                    <CardHeader className="flex-row items-center gap-4 space-y-0">
                        <TrendingUp className="h-8 w-8 text-primary" />
                        <div className="flex-1">
                            <CardTitle>YTD Expenses</CardTitle>
                            <CardDescription>{ytdLabel}</CardDescription>
                        </div>
                        <div className="text-4xl font-bold text-primary">{formatCurrency(ytdTotal)}</div>
                    </CardHeader>
                </Card>
            </div>
        </div>
      </main>
      <footer className="py-4 text-center text-sm text-foreground z-20 bg-background/50 backdrop-blur-sm">
        &copy; {new Date().getFullYear()} Studio Shizuka
      </footer>
    </div>
  );
}

export default function Home() {
  return (
    <FirebaseClientProvider>
      <HomePageContent />
    </FirebaseClientProvider>
  );
}
